<template>
  <iColumn
    v-if="previewsBothStates"
    gap="extraSmall"
    width="fill"
    height="hug"
    data-test="required-gate-preview"
  >
    <AppComponentOverrideHost
      v-for="componentId in previewComponentIds"
      :key="componentId"
      :app-name="questionnaireAppName"
      :dynamic-component-id="componentId"
      :contexts="previewContexts"
      :theme-mode="themeMode"
    />
  </iColumn>
  <AppComponentOverrideHost
    v-else-if="resolvedComponentId && resolvedContexts"
    :app-name="questionnaireAppName"
    :dynamic-component-id="resolvedComponentId"
    :contexts="resolvedContexts"
    :theme-mode="themeMode"
  />
  <iText
    v-else-if="editMode"
    variant="subtle"
    font-size="small"
    data-test="required-gate-empty"
  >
    {{ emptyStateMessage }}
  </iText>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import AppComponentOverrideHost
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/AppComponentOverrideHost.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { resolveBoundEntityId } from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import { declaredAppOption, declaredAppOptions } from "%/dynamic-component-editor/options/declared-app-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import { REQUIRED_GATE_STATES_OPTION_KEY } from "+/QuizzesPolls/Components/questionnaire-card-selection";
import { QUIZZES_POLLS_APP_NAME } from "+/QuizzesPolls/app-name";

const READY_CARD_KEY = "required-gate-ready";
const BLOCKED_CARD_KEY = "required-gate-blocked";

const SUBMISSION_CONTEXT_NAME = "submission";
const SUBMISSION_ENTITY_TYPE = "Submission";
const ANSWER_ENTITY_TYPE = "Answer";
const QUESTION_ENTITY_TYPE = "Question";
const ANSWERS_FIELD = "answers";
const QUESTION_FIELD = "question";
const STATUS_ACTIVE = "active";
const DEFAULT_APP_NAME = "QuizzesPolls";

const MSG_NO_CONTEXT = "Drop this inside a submission card, so it has a submission to read.";
const MSG_NO_READY = "No component set for the ready-to-submit state.";
const MSG_NO_BLOCKED = "No component set for the outstanding-required state.";

export default {
  name: "RequiredAnswersGate",
  components: { AppComponentOverrideHost },
  mixins: [DynamicElementMixin],
  inject: {
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
  },
  elementPanelSettings: {
    displayName: "Submit Gate",
    displayIcon: "mdi:lock-check-outline",
    elementType: "element",
    elementGroup: "general",
  },
  options: [
    ...declaredAppOptions(QUIZZES_POLLS_APP_NAME, [
      REQUIRED_GATE_STATES_OPTION_KEY,
      READY_CARD_KEY,
      BLOCKED_CARD_KEY,
    ]),
  ],
  optionBundles: [new AppearanceOptions()],
  computed: {
    questionnaireAppName() {
      return this.elementContext?.appName || DEFAULT_APP_NAME;
    },
    questionnaireApp() {
      try {
        return App.get(this.questionnaireAppName);
      } catch {
        return null;
      }
    },
    elementContext() {
      return this.optionValues?.context || this.configuration?.options?.context || {};
    },
    submissionEntityId() {
      return resolveBoundEntityId(this, SUBMISSION_CONTEXT_NAME) || 0;
    },
    answerEntityIds() {
      if (!this.submissionEntityId || !this.questionnaireApp) {
        return [];
      }
      const items = this.questionnaireApp.getFeedItems(
        SUBMISSION_ENTITY_TYPE,
        { id: this.submissionEntityId },
        ANSWERS_FIELD,
      ) ?? [];
      return items
          .map(item => item?.entityPk ?? item?.pk ?? null)
          .filter(Boolean)
          .map(entityPk => Number(entityPk.id) || 0)
          .filter(Boolean);
    },
    outstandingRequiredCount() {
      return this.answerEntityIds.filter(answerEntityId => {
        if (this.answerField(answerEntityId, "status") !== STATUS_ACTIVE) {
          return false;
        }
        const questionEntityId = this.questionEntityIdFor(answerEntityId);
        if (!questionEntityId) {
          return false;
        }
        if (String(this.questionField(questionEntityId, "prompt") ?? "").trim() === "") {
          return false;
        }
        return this.questionField(questionEntityId, "required") === true
          && this.answerField(answerEntityId, "isAnswered") !== true;
      }).length;
    },
    resolvedComponentId() {
      return this.componentIdForKey(
        this.outstandingRequiredCount > 0 ? BLOCKED_CARD_KEY : READY_CARD_KEY,
      );
    },
    resolvedContexts() {
      if (!this.submissionEntityId) {
        return null;
      }
      return {
        [SUBMISSION_CONTEXT_NAME]: {
          entityType: SUBMISSION_ENTITY_TYPE,
          pk: { id: this.submissionEntityId },
        },
      };
    },
    previewsBothStates() {
      return Boolean(this.editMode) && !this.resolvedContexts && this.previewComponentIds.length > 0;
    },
    previewComponentIds() {
      return [BLOCKED_CARD_KEY, READY_CARD_KEY]
          .map(key => this.componentIdForKey(key))
          .filter(componentId => componentId > 0);
    },
    previewContexts() {
      return {
        [SUBMISSION_CONTEXT_NAME]: { entityType: SUBMISSION_ENTITY_TYPE, pk: { id: 0 } },
      };
    },
    emptyStateMessage() {
      if (!this.resolvedContexts) {
        return MSG_NO_CONTEXT;
      }
      return this.outstandingRequiredCount > 0 ? MSG_NO_BLOCKED : MSG_NO_READY;
    },
  },
  methods: {
    componentIdForKey(key) {
      const optionClass = declaredAppOption(QUIZZES_POLLS_APP_NAME, key);
      if (!optionClass) {
        return 0;
      }
      return Number(this.getOptionValue(optionClass)) || 0;
    },
    answerField(answerEntityId, fieldName) {
      if (!answerEntityId || !this.questionnaireApp) {
        return null;
      }
      try {
        return this.questionnaireApp.getEntityField(
          ANSWER_ENTITY_TYPE, { id: answerEntityId }, fieldName,
        );
      } catch {
        return null;
      }
    },
    questionEntityIdFor(answerEntityId) {
      const question = this.answerField(answerEntityId, QUESTION_FIELD);
      return Number(question?.pk?.id ?? question?.id ?? 0);
    },
    questionField(questionEntityId, fieldName) {
      if (!questionEntityId || !this.questionnaireApp) {
        return null;
      }
      try {
        return this.questionnaireApp.getEntityField(
          QUESTION_ENTITY_TYPE, { id: questionEntityId }, fieldName,
        );
      } catch {
        return null;
      }
    },
  },
};
</script>
